/**
 * Web Component entry point for the Community Posts widget.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/community-posts-web-component';
 *
 * // Then use in HTML:
 * // <legion-community-posts api-url="https://your-domain.com" auth-token="jwt" />
 * ```
 */

import { POSTS_THEMING_PROP_TO_PARAM } from "./types";
import {
  attachAuthBridge,
  type AuthBridgeHandle,
  AUTH_BRIDGE_OBSERVED_ATTRS,
} from "../shared/auth-bridge";

const POSTS_CSS_ATTRIBUTES = Object.values(POSTS_THEMING_PROP_TO_PARAM);

class LegionCommunityPostsElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "api-url", "page-size", "filter", "channel-id",
      "show-filters", "show-actions", "user-id", "username", "theme",
      "width", "height",
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
      ...POSTS_CSS_ATTRIBUTES,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.apiUrl,
    });
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if ((AUTH_BRIDGE_OBSERVED_ATTRS as readonly string[]).includes(name) && this.iframe) {
      this.authBridge?.postCurrentToken();
      return;
    }
    this.render();
  }

  private get apiUrl(): string { return this.getAttribute("api-url") || window.location.origin; }
  private get pageSize(): number { return Math.min(Math.max(parseInt(this.getAttribute("page-size") || "10", 10), 5), 50); }
  private get filter(): string { return this.getAttribute("filter") || "recent"; }
  private get channelId(): string | null { return this.getAttribute("channel-id"); }
  private get showFilters(): boolean { return this.getAttribute("show-filters") !== "false"; }
  private get showActions(): boolean { return this.getAttribute("show-actions") !== "false"; }
  private get userId(): string | null { return this.getAttribute("user-id"); }
  private get username(): string | null { return this.getAttribute("username"); }
  private get theme(): string { return this.getAttribute("theme") || "light"; }
  private get widgetWidth(): string { return this.getAttribute("width") || "100%"; }
  private get widgetHeight(): string { return this.getAttribute("height") || "800"; }

  render(): void {
    if (!this.shadowRoot) return;

    const iframe = document.createElement("iframe");
    const url = new URL("/widget/community-posts", this.apiUrl);

    url.searchParams.set("pageSize", String(this.pageSize));
    url.searchParams.set("filter", this.filter);
    url.searchParams.set("showFilters", String(this.showFilters));
    url.searchParams.set("showActions", String(this.showActions));
    url.searchParams.set("theme", this.theme);

    if (this.channelId) url.searchParams.set("channelId", this.channelId);
    if (this.userId) url.searchParams.set("userId", this.userId);
    if (this.username) url.searchParams.set("username", this.username);

    POSTS_CSS_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(attr, val);
      }
    });

    iframe.src = url.toString();
    iframe.width = this.widgetWidth;
    iframe.height = this.widgetHeight;
    iframe.frameBorder = "0";
    iframe.scrolling = "no";
    iframe.title = "Community Posts Widget";

    iframe.onload = () => {
      this.authBridge?.postCurrentToken();
      this.dispatchEvent(new CustomEvent("load", {
        detail: { widget: "community-posts", theme: this.theme },
      }));
    };

    iframe.onerror = () => {
      if (!this.shadowRoot) return;
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load community posts widget</div>`;
    };

    const style = document.createElement("style");
    style.textContent = `
      :host { display: block; width: ${this.widgetWidth.includes("%") ? this.widgetWidth : this.widgetWidth + "px"}; height: ${this.widgetHeight}px; }
      iframe { width: 100%; height: 100%; border: none; border-radius: 8px; box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1); background: ${this.theme === "dark" ? "#1a1a1a" : "white"}; }
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }
}

if (typeof customElements !== "undefined" && !customElements.get("legion-community-posts")) {
  customElements.define("legion-community-posts", LegionCommunityPostsElement);
}

export { LegionCommunityPostsElement };

